/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  DataMapper,
  entity,
  Enum,
  FilteredDataQuery,
  identity,
  optimisticLockingOf,
  RequestMethod,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { map$, switchMap$ } from '@gh/rx/operators';
import { ContactDetailsMapper, ContactDuplicateFilterMapper } from './mappers/contact-details-mapper';
import {
  ContactCustomerListItemMapper,
  ContactListFilterMapper,
  ContactListInitMapper,
  ContactListItemMapper,
  ContactSupplierListItemMapper,
  ContactVendorListItemMapper,
} from './mappers/contact-list-item.mapper';
import {
  ContactDuplicateCheckDto,
  ContactDuplicateFilterDto,
  ContactDuplicateItemDto,
} from './model/contact-details-common.dto';
import { ContactDetailsDto } from './model/contact-details.dto';
import {
  ContactCustomerListFilterDto,
  ContactCustomerListItemDto,
  ContactListFilterDto,
  ContactListInitDto,
  ContactListItemDto,
  ContactSupplierListFilterDto,
  ContactSupplierListItemDto,
  ContactVendorListItemDto,
} from './model/contact-list-item.dto';
import { ContactType } from './model/contact-type.enum';
import { DictionaryService, globalDictionaries } from '@gh/common-shared';
import { combineLatest$ } from '@gh/rx';
import { ContactDetailsInvitationSubTabDto } from './model/contact-details-customer.dto';

export interface Resource {
  contactId: ID;
  name: string;
  isPreferred: boolean;
  isCcSupplier: boolean;
  isEmployee: boolean;
}

@Injectable()
export class ContactRestService extends AbstractRestService {
  constructor(settings: RestSettings, public dictionaryService: DictionaryService) {
    super(settings);
  }

  getInit(): Observable<ContactListInitDto> {
    return this.httpGet(`${this.bp}/contacts/init-search`, ContactListInitMapper);
  }

  getNew(): Observable<ContactDetailsDto> {
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) => this.httpGet(`${this.bp}/contacts/init`, mapper)));
  }

  get(id: ID): Observable<ContactDetailsDto> {
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) => this.httpGet(`${this.bp}/contacts/${id}`, optimisticLockingOf(mapper))));
  }

  create(details: ContactDetailsDto): Observable<ContactDetailsDto> {
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) => this.httpPost(`${this.bp}/contacts`, details, optimisticLockingOf(mapper))));
  }

  update(details: ContactDetailsDto): Observable<ContactDetailsDto> {
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) =>
                                  this.httpPut(`${this.bp}/contacts/${details.id}`, details, optimisticLockingOf(mapper))));
  }

  inviteToPortal(id: ID, email: string, type: ContactType): Observable<ContactDetailsDto> {
    const details = {
      email,
      type: Enum.toValue(ContactType, type),
    };
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) => this.httpPost(
                                    `${this.bp}/contacts/${id}/invite`,
                                    details,
                                    identity(),
                                    optimisticLockingOf(mapper))));
  }

  resendInviteToPortal(
    id: ID,
    invitation: ContactDetailsInvitationSubTabDto,
    type: ContactType): Observable<ContactDetailsDto> {
    const details = {
      invitationId: invitation.invitationId,
      email: invitation.invitationEmail,
      type: Enum.toValue(ContactType, type),
    };
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) => this.httpPost(`${this.bp}/contacts/${id}/invite-resend`,
                                                          details,
                                                          identity(),
                                                          optimisticLockingOf(mapper))));
  }
  deleteInvitation(id: ID,
                   invitation: ContactDetailsInvitationSubTabDto,
                   type: ContactType): Observable<any> {
    const details = {
      invitationId: invitation.invitationId,
      email: invitation.invitationEmail,
      type: Enum.toValue(ContactType, type),
    };
    return this.getContactDetailsMapper()
               .pipe(switchMap$((mapper) => this.httpDelete(`${this.bp}/contacts/${id}/delete-invite`,
                                                            details,
                                                            identity(),
                                                            optimisticLockingOf(mapper))));
  }
  checkDuplicates(filter: ContactDuplicateFilterDto): Observable<ContactDuplicateCheckDto> {
    return this.httpGet(
      `${this.bp}/contacts/check-duplicates`,
      filter,
      ContactDuplicateFilterMapper,
      entity<ContactDuplicateCheckDto>());
  }

  findDuplicates(filter: ContactDuplicateFilterDto): Observable<ContactDuplicateItemDto[]> {
    return this.httpGet(
      `${this.bp}/contacts/duplicates`,
      filter,
      ContactDuplicateFilterMapper,
      arrayOf(entity<ContactDuplicateItemDto>()));
  }

  findAllAsDataSource(filter: ContactListFilterDto): SlicedDataSource<ContactListItemDto> {
    return this.createSlicedDataSource({
      method: RequestMethod.Post,
      url: `${this.bp}/contacts/list`,
      body: filter,
      requestMapper: ContactListFilterMapper,
      responseMapper: arrayOf(ContactListItemMapper),
    });
  }

  findAllCustomersAsDataSource(filter: ContactCustomerListFilterDto): SlicedDataSource<ContactCustomerListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/contacts/customers`,
      filter,
      identity(),
      arrayOf(ContactCustomerListItemMapper));
  }

  findAllSuppliersAsDataSource(filter: ContactSupplierListFilterDto): SlicedDataSource<ContactSupplierListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/contacts/suppliers`,
      filter,
      identity(),
      arrayOf(ContactSupplierListItemMapper));
  }

  findAllResourcesAsDataSource(filter: ContactSupplierListFilterDto): SlicedDataSource<Resource> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/contacts/resources`,
      filter,
      identity(),
      identity(),
      );
  }

  findAllVendorsAsDataSource(query: FilteredDataQuery<string>,
                             excludedIds: ID[]): SlicedDataSource<ContactVendorListItemDto> {
    return this.createSlicedDataSourcePost(
      `${this.bp}/contacts/vendors`,
      { name: query.filter, excludedIds },
      identity(),
      arrayOf(ContactVendorListItemMapper));
  }

  syncWithXero(id: ID): Observable<any> {
    return this.httpPost(`${this.bp}/contacts/sync-with-xero`, {id}, identity());
  }

  validate(id: ID): Observable<any> {
    return this.httpPost(
      `${this.bp}/contacts/${id}/validate`,
      {},
      identity(),
      identity(),
    );
  }

  checkResourceUsage(taskId: ID): Observable<any> {
    return this.httpGet(`${this.bp}/scheduled-resource-usage/${taskId}`, identity());
  }

  private getContactDetailsMapper(): Observable<DataMapper<ContactDetailsDto, any>> {
    return combineLatest$(this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.gender),
                          this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.paidBy),
                          this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.payBasis),
                          this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.employeePayroll),
                          )
      .pipe(
        map$(
          ([gender, paidBy, payBasis, payRoll]) =>
            ContactDetailsMapper({
              gender: this.dictionaryService.getEnumByDictionary(globalDictionaries.gender, gender),
              paidBy: this.dictionaryService.getEnumByDictionary(globalDictionaries.paidBy, paidBy),
              payBasis: this.dictionaryService.getEnumByDictionary(globalDictionaries.payBasis, payBasis),
              payRoll: this.dictionaryService.getEnumByDictionary(globalDictionaries.employeePayroll, payRoll),
            })));
  }
}
